/**
 * Article-only presentation of FormZero's per-row save state.
 *
 * The Save action retains its floppy-disk icon; its colour communicates state.
 * A small, sticky status line at the START of the accordion body gives context
 * for long content editors without modifying the toolbar layout.
 */
export default class ArticleComponentFeedback {
    constructor(translate = (message) => message) {
        this.translate = translate;
        this.elements = new WeakMap();
    }

    /** Called by FormZeroAjax's optional onRowStatus hook. */
    update({row, trigger, state, message = ''}) {
        if (!row || !trigger || !['idle', 'dirty', 'saving', 'saved', 'error'].includes(state)) return;
        const parts = this.ensure(row);
        if (!parts) return;
        const labels = {
            dirty: 'article.unsavedChanges',
            saving: 'article.savingChanges',
            saved: 'article.fragmentSaved',
            error: 'article.saveFailed',
        };
        const caption = message || (state === 'idle' ? '' : this.translate(labels[state]));
        if (row.dataset.articleSaveState === state && parts.label.textContent === caption) return;

        row.dataset.articleSaveState = state;
        trigger.dataset.articleSaveState = state;
        parts.label.textContent = caption;
        parts.banner.setAttribute('role', state === 'error' ? 'alert' : 'status');
        parts.banner.setAttribute('aria-live', state === 'error' ? 'assertive' : 'polite');
        // The Save button already has an accessible label. Append the state via
        // aria-describedby rather than injecting a dot, text, or extra controls.
        if (caption) parts.banner.setAttribute('title', caption);
        else parts.banner.removeAttribute('title');
    }

    ensure(row) {
        const existing = this.elements.get(row);
        if (existing) return existing;
        // Select a DIRECT child body so a parent's banner never ends up inside
        // a nested Content / Link / Partner form.
        const body = row.querySelector?.(':scope > .accordion-collapse > .accordion-body');
        if (!body || typeof document === 'undefined' || typeof document.createElement !== 'function') return null;

        const banner = document.createElement('div');
        banner.className = 'article-component-feedback';
        banner.id = `article-save-feedback-${row.id}`;
        banner.dataset.articleFeedback = '';
        const trigger = [...(row.querySelectorAll?.('[data-fz-action="save"]') ?? [])]
            .find((candidate) => candidate.dataset?.fzRow === `#${row.id}`);
        if (trigger) {
            const previous = trigger.getAttribute?.('aria-describedby') ?? '';
            const ids = new Set(previous.split(/\s+/).filter(Boolean));
            ids.add(banner.id);
            trigger.setAttribute?.('aria-describedby', [...ids].join(' '));
        }
        banner.setAttribute('role', 'status');
        banner.setAttribute('aria-live', 'polite');
        banner.setAttribute('aria-atomic', 'true');

        const label = document.createElement('span');
        label.className = 'article-component-feedback__label';
        banner.append(label);
        // Visible immediately when a component opens, and sticky for long text.
        body.prepend(banner);

        const parts = {banner, label};
        this.elements.set(row, parts);
        return parts;
    }
}
